import React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';
import { Box, Link, Paper, Divider, IconButton, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import Container from '../../../../components/Container';
import Main from '../../../../layouts/Main';
import BankList from './components/BankList';
import InfoDrawer from './components/InfoDrawer';

import BankDepository from './components/BankDepository';
import BankNonDepository from './components/BankNonDepository';
import LocalFilerInfo from './components/LocalFilerInfo';
import LocalLPCInfo from './components/LocalLPCInfo';
import RegisterInfo from './components/RegisterInfo';
import RegisterInfoBallotQuestion from './components/RegisterInfoBallotQuestion';
import RegisterInfoIE from './components/RegisterInfoIE';
import RegisterInfoIEPAC from './components/RegisterInfoIEPAC';
import ReporterInfo from './components/ReporterInfo';
import TreasurerInfo from './components/TreasurerInfo';
import TreasurerTrainingInfo from './components/TreasurerTrainingInfo';


//Lists the type of filer, their subtitle, and description
const gettingStartedData: { filerId: number, filerType: string, filerSubtitle: string, filerDescription: string }[] = [
  { filerId: 1, filerType: "Statewide", filerSubtitle: "candidates for Statewide office", filerDescription: "Statewide Offices: Governor, Lieutenant Governor, Attorney General, Secretary of State, Treasurer, Auditor." },
  { filerId: 2, filerType: "Governor's Council", filerSubtitle: "candidates for Governor's Council", filerDescription: "Governor's Council: 1st / 2nd / 3rd / 4th / 5th / 6th / 7th / 8th District." },
  { filerId: 3, filerType: "House and Senate", filerSubtitle: "candidates for House or Senate", filerDescription: "House & Senate: A candidate for the General Court is seeking a seat in the State House or State Senate." },
  { filerId: 4, filerType: "County and District", filerSubtitle: "candidates for County and District offices", filerDescription: "County and District Offices: County Clerks of Court, Commissioners & Treasurers; Registers of Deeds & Probate; District Attorneys; Sheriffs and Suffolk County Clerks of Superior & Supreme Judicial Courts; Governor's Council." },
  { filerId: 5, filerType: "Mayoral", filerSubtitle: "candidates in all cities in the Commonwealth of Massachusetts", filerDescription: "Candidates for mayor in all cities in MA file their campaign finance reports with OCPF." },
  { filerId: 6, filerType: "City Council", filerSubtitle: "candidates in cities with populations of more than 65,000", filerDescription: "City Councilor in Boston, Brockton, Cambridge, Fall River, Framingham, Haverhill, Lawrence, Lowell, Lynn, Malden, New Bedford, Newton, Quincy, Somerville, Springfield, Waltham and Worcester." },
  { filerId: 7, filerType: "Local Offices", filerSubtitle: "all municipal offices with reporting requirements to local election officials", filerDescription: "Such offices include select board and school committee, as well as city council in cities with populations of less than 65,000." },
  { filerId: 8, filerType: "Political Action Committees (PACs) that file with OCPF", filerSubtitle: "raise and spend money to support or oppose candidates", filerDescription: "A political action committee, or “PAC”, is a political committee that is organized to support or oppose a candidate or a slate of candidates who file with OCPF. Therefore, a PAC could be organized to support or oppose a particular candidate. It could also be organized to support or oppose a variety of candidates of a particular party or who support a particular cause. A PAC is separate from a candidate’s committee." },
  { filerId: 9, filerType: "State Ballot Question Committees", filerSubtitle: "for questions appearing on the state election ballot", filerDescription: "A Ballot Question Committee is a person or group of people who raise or spend money to support or oppose a question put to voters at a state election." },
  { filerId: 10, filerType: "Independent Expenditure PACs", filerSubtitle: "for independent expenditure committees", filerDescription: "An IEPAC is a political action committee that receives contributions to make independent expenditures, and only makes independent expenditures." },
  { filerId: 11, filerType: "Segregated Fund Accounts", filerSubtitle: "for candidates wanting to establish recount, legal defense or inaugural funds, and state party committees wanting to establish legal defense funds", filerDescription: "There are three types of funds: An Inauguration Fund, A Recount Fund, and A Legal Defense Fund." },
  { filerId: 12, filerType: "Independent Spending Reports", filerSubtitle: "Register to file Independent Spending Reports", filerDescription: "There are three types of independent spending reports: Independent Expenditures, Electioneering Communications(EC), Ballot Question Spending." },
  { filerId: 13, filerType: "Municipal Ballot Question Committees", filerSubtitle: "for questions appearing on city or town ballot", filerDescription: "Committees that raise or spend money to support or oppose a question put to voters in a municipal election." },
  { filerId: 14, filerType: "Local Political Party Committees", filerSubtitle: "ward, town or city committees of a state political party", filerDescription: "M.G.L. Chapter 52 regulates the process by which local political party committees are formed. Ward and town committee members of each party are elected at the presidential primary by those who have voted in that particular party. The members of the various ward committees of a political party in a city constitute the city committee." },
  { filerId: 15, filerType: "Local Political Action Committees", filerSubtitle: "for committees concerned with city or town candidates who do not file with OCPF", filerDescription: "Committees that raise and spend money to support or oppose one or more city or town candidates who do not file with OCPF, based upon stated principles or interests." },
  { filerId: 16, filerType: "Local Independent Expenditure PACs", filerSubtitle: "for independent expenditure committees supporting or opposing candidates who file locally on the municipal level", filerDescription: "A local IEPAC organizes to support or oppose candidates who file with the local election official, such as school committee and selectboard." }
];


//Array of steps for each filer type (a step set)
//Each step has text, an info link (optional), and an object named linksAndTextReplacements (optional)
//which has a placeholder string as well as a dictionary of key value pairs called replacements
//the keys correspond to filer type and the values correspond to an array of strings of length 2 that has 
// a url as the first index and text as the second index
//For the info link, the key is the filer type and the value is the info type to display in the InfoDrawer
//By default, the info type is 'default' unless otherwise specified

//the placeholder is what will be replaced and must be in the text string
//If only the first index of the array is set (which is the url) then the placeholder will be replaced with the url
//If only the second index of the array is set (which is the text) then the placeholder will be replaced with the text
//If both indexes of the array are set then the placeholder will be replaced with the text which is a link to the url 
//If no steps.text is populated but the linksAndTextReplacements object exists a conditional step depending on the filer type key will be created
//This is here specifically for step sets that are the same for different filer types but may have an additional step e.g. local offices and local PACs
const stepsAccordingToFilerType: {
  stepSetId: number,
  steps: {
    text: string,
    linksAndTextReplacements?: { placeholder: string, replacements: Record<string, string[]> }[],
    infoType?: Record<string, string>
  }[],
}[] = [
    {
      stepSetId: 1,
      steps: [
        {
          text: "Click here to organize your committee with OCPF.",
          linksAndTextReplacements: [
            {
              placeholder: "here",
              replacements: {
                default: ["https://www.ocpfreporter.us/onlineorganization/signup"]
              }
            }
          ],
          infoType: { default: "RegisterInfo" }
        },
        {
          text: "Complete CPF D103 Form to set up your Depository Bank Account.",
          linksAndTextReplacements: [
            { placeholder: "CPF D103 Form", replacements: { default: ["https://ocpf2.blob.core.windows.net/pdf/forms/cpf_d103.pdf"] } }
          ],
          infoType: { default: 'BankDepository' }
        },
        {
          text: "Complete Online Treasurer Training.",
          linksAndTextReplacements: [
            { placeholder: "Treasurer Training", replacements: { default: ["https://www.ocpfreporter.us/"] } }
          ],
          infoType: { default: 'TreasurerTrainingInfo' }
        },
        {
          text: "Start using OCPF Reporter to prepare and file your campaign finance reports. Checklist.",
          linksAndTextReplacements: [
            { placeholder: "Reporter", replacements: { default: ["https://ocpfreporter.us/"] } },
            { placeholder: "Checklist", replacements: { default: ["/placeholder-checklist-url"] } }
          ],
          infoType: { default: 'ReporterInfo' }
        }
      ]
    },
    {
      stepSetId: 2,
      steps: [
        {
          text: "Click here to organize your committee with OCPF.",
          linksAndTextReplacements: [{ placeholder: "here", replacements: { default: ["https://www.ocpfreporter.us/onlineorganization/signup"] } }],
          infoType: { default: 'RegisterInfo' }
        },
        {
          text: "Complete CPF D103 Form to set up your Depository Bank Account.",
          linksAndTextReplacements: [
            { placeholder: "CPF D103 Form", replacements: { default: ["https://ocpf2.blob.core.windows.net/pdf/forms/cpf_d103.pdf"] } }
          ],
          infoType: { default: 'BankDepository' }
        },
        {
          text: "Complete Online Treasurer Training.",
          linksAndTextReplacements: [
            { placeholder: "Treasurer Training", replacements: { default: ["https://ocpfreporter.us/"] } }
          ],
          infoType: { default: 'TreasurerTrainingInfo' }
        },
        {
          text: "Start using OCPF Reporter to prepare and file your campaign finance reports.",
          linksAndTextReplacements: [
            { placeholder: "Reporter", replacements: { default: ["https://ocpfreporter.us/"] } }
          ],
          infoType: { default: 'ReporterInfo' }
        }
      ]
    },
    {
      //
      //Independent Expenditure PACs
      stepSetId: 3,
      steps: [
        {
          text: "Click here to organize your committee with OCPF.",
          linksAndTextReplacements: [{ placeholder: "Click here", replacements: { default: ["https://www.ocpfreporter.us/onlineorganization/signup"] } }],
          infoType: { 'State Ballot Question Committees': 'RegisterInfoBallotQuestion', 'Independent Expenditure PACs': 'RegisterInfoIEPAC', default: 'RegisterInfo' }
        },
        {
          text: "Set up your Bank Account.",
          infoType: { default: "BankNonDepository" }
        },
        {
          text: "Complete Online Treasurer Training.",
          linksAndTextReplacements: [
            { placeholder: "Treasurer Training", replacements: { default: ["https://ocpfreporter.us/"] } }
          ],
          infoType: { default: 'TreasurerTrainingInfo' }
        },
        {
          text: "Start using OCPF Reporter to prepare and file your campaign finance reports.",
          linksAndTextReplacements: [
            { placeholder: "Reporter", replacements: { default: ["https://ocpfreporter.us/"] } }
          ],
          infoType: { default: 'ReporterInfo' }
        }
      ]
    },
    {
      stepSetId: 4,
      steps: [
        {
          text: "Appoint a contact person for your segregated fund account."
        },
        {
          text: "Download and complete a Statement of Organization for your fund: Form CPF 101 SFA, CPF M 101 SFA",
          linksAndTextReplacements: [
            { placeholder: "Form CPF 101 SFA", replacements: { default: ["https://ocpf2.blob.core.windows.net/pdf/forms/cpf_101_sfa.pdf"] } },
            { placeholder: "CPF M 101 SFA", replacements: { default: ["https://ocpf2.blob.core.windows.net/pdf/forms/cpf_m101_sfa.pdf"] } }
          ]
        },
        {
          text: "Email, fax or bring in the completed CPF 101 SFA form to OCPF."
        },
        {
          text: "Set up your Bank Account.",
          infoType: { default: "BankNonDepository" }
        },
        {
          text: "Start using OCPF Reporter to prepare and file your campaign finance reports.",
          linksAndTextReplacements: [
            { placeholder: "Reporter", replacements: { default: ["https://ocpfreporter.us/"] } }
          ],
          infoType: { default: 'ReporterInfo' }
        }
      ]
    },
    {
      stepSetId: 5,
      steps: [
        {
          text: "Click here to register with OCPF.",
          linksAndTextReplacements: [{ placeholder: "Click here", replacements: { default: ["https://www.ocpfreporter.us/Account/Register"] } }],
          infoType: { default: 'RegisterInfoIE' }
        },
        {
          text: "Start using OCPF Reporter to prepare and file an Independent Spending Report.",
          linksAndTextReplacements: [
            { placeholder: "Reporter", replacements: { default: ["https://ocpfreporter.us/"] } }
          ],
          infoType: { default: 'ReporterInfo' }
        }
      ]
    },
    {
      stepSetId: 6,
      steps: [
        {
          text: "Download and complete a Statement of Organization Form {form}.",
          linksAndTextReplacements: [{
            placeholder: "{form}", replacements:
            {
              default: ["#"],
              'Local Offices': ['https://ocpf2.blob.core.windows.net/pdf/forms/cpf_m101_18_new.pdf', 'CPF M 101'],
              'Municipal Ballot Question Committees': ['https://ocpf2.blob.core.windows.net/pdf/forms/cpf_m101_bq.pdf', 'CPF M 101 BQ'],
              'Local Political Action Committees': ['https://ocpf2.blob.core.windows.net/pdf/forms/cpf_m101_pc.pdf', 'CPF M 101 PC']
            }
          }]
        },
        {
          //an example of a conditional step (only for the listed filer types)
          text: "",
          linksAndTextReplacements: [{
            placeholder: "", replacements:
            {
              'Municipal Ballot Question Committees': ['', 'Appoint a Chair and a Treasurer for your committee.'],
              'Local Political Action Committees': ['', 'Appoint a Chair and a Treasurer for your committee.']
            }
          }],
          infoType: { default: 'TreasurerInfo' }
        },
        {
          text: "Bring the completed {form} to your local election official.",
          linksAndTextReplacements: [{
            placeholder: "{form}", replacements:
            {
              default: ["#"],
              'Local Offices': ['', 'CPF M 101'],
              'Local Political Action Committees': ['', 'CPF M 101 PC'],
              'Municipal Ballot Question Committees': ['', 'CPF M 101 BQ']
            }
          }]
        },
        {
          text: "Set up your Bank Account.",
          infoType: { default: 'BankNonDepository' }
        },
        {
          //an example of a conditional step (only for the lsited filer types)

           text: "Complete online training.",
          linksAndTextReplacements: [
            { placeholder: "training", replacements: { default: ["https://www.ocpf.us/municipal/treasurertrainings"] } }
          ],
        },
        {
          text: "Prepare and file your reports with your local election official. Candidate Checklist. Click here to register to use OCPF\’s software, Reporter 7, to create, print and file campaign finance reports. Reporter 7 Illustrated guide.",
          linksAndTextReplacements:
            [
              {
                placeholder: "Candidate Checklist",
                replacements:
                {
                  default: ["", ""],
                  'Local Offices': ['https://ocpf.us/PublicSearch/ViewDocument?id=3599']
                }
              },
              {
                placeholder: "here",
                replacements:
                {
                  default: ["", ""],
                  'Local Offices': ['https://ocpfreporter.us/LocalFilerSignup/']
                }
              },
               {
                placeholder: "Reporter 7 Illustrated guide",
                replacements:
                {
                  default: ["", ""],
                  'Local Offices': ['https://ocpf.us/PublicSearch/ViewDocument?id=3350']
                }
              },

              {
                placeholder: "to register to use OCPF\’s software, Reporter 7, to create, print and file campaign finance reports.",
                replacements:
                {
                  default: ["", ""],
                  'Local Offices': ['', 'to register to use OCPF\’s software, Reporter 7, to create, print and file campaign finance reports.']
                }
              }
            ],
          infoType: { default: 'LocalFilerInfo' }
        }
      ]
    },
    {
      stepSetId: 7,
      steps: [
        {
          text: "Click here for the form to organize your committee with the local election official.",
          linksAndTextReplacements: [{ placeholder: "Click here", replacements: { default: ["https://ocpf2.blob.core.windows.net/pdf/forms/m101iepac.pdf?t=fTkFY15"] } }]
        },
        {
          text: "Set up your Bank Account."
        },
        {
          text: "Click here for the local IEPAC guide.",
          linksAndTextReplacements: [{ placeholder: "Click here", replacements: { default: ["https://www.ocpf.us/PublicSearch/ViewDocument?id=3755"] } }]
        },
        {
          text: "Use the M 102 IEPAC form to file your campaign finance reports.",
          linksAndTextReplacements: [{ placeholder: "M 102 IEPAC form", replacements: { default: ["https://ocpf2.blob.core.windows.net/pdf/forms/M102_edit.pdf"] } }]
        }
      ]
    },
    {
      stepSetId: 8,
      steps: [
        {
          text: "Download and complete a Statement of Organization Form {form}.",
          linksAndTextReplacements: [{
            placeholder: "{form}", replacements:
              { default: ["https://ocpf2.blob.core.windows.net/pdf/forms/cpf_101_wtc.pdf", "CPF 101 WTC"] }
          }],
        },
        {
          text: "Appoint a Chair and a Treasurer for your committee.",
          infoType: { default: 'TreasurerInfo' }
        },
        {
          text: "Bring the completed {form} to your local election official.",
          linksAndTextReplacements: [{
            placeholder: "{form}", replacements:
              { default: ["", 'CPF 101 WTC'] }
          }]
        },
        {
          //an example of a conditional step (only for Local Political Party Committees)
          text: "Email or fax the copies of the completed CPF 101 WTC form to OCPF.",
        },
        {
          text: "Set up your Bank Account.",
          infoType: { default: 'BankNonDepository' }
        },
        {
          text: "Complete Online Treasurer Training.",
          linksAndTextReplacements: [
            { placeholder: "Treasurer Training", replacements: { default: ["https://ocpfreporter.us/"] } }
          ],
          infoType: { default: 'TreasurerTrainingInfo' }
        },
        {
          text: "Start using OCPF Reporter to prepare and file your campaign finance reports.",
          linksAndTextReplacements: [
            { placeholder: "Reporter", replacements: { default: ["https://ocpfreporter.us/"] } }
          ],
          infoType: { default: 'LocalLPCInfo' }
        }
      ]
    }
  ];

//dictionary to map filerType to step set
//First number is filerId, second number is stepSetId
const filerTypeToStep: Record<number, number> = {
  1: 1,//Statewide
  2: 2, //Governor's Council
  3: 1, //House and Senate
  4: 2, //County and District
  5: 2, //Mayoral
  6: 2, //City Council
  7: 6, //Local Offices
  8: 1, //Political Action Committees (PACs) that File with OCPF
  9: 3, //State Ballot Question Committees
  10: 3, //Independent Expenditure PACs
  11: 4, //Segregated Fund Accounts
  12: 5, //Independent Spending Reports
  13: 6, //Municipal Ballot Question Committees
  14: 8, //Local Political Party Committees
  15: 6, //Local Political Action Committees
  16: 7 //Local Independent Expenditure PACs
};

const componentMap: Record<string, JSX.Element> = {
  'BankDepository': <div><BankDepository /> <BankList /></div>,
  'BankNonDepository': <BankNonDepository />,
  'LocalFilerInfo': <LocalFilerInfo />,
  'LocalLPCInfo': <LocalLPCInfo />,
  'RegisterInfo': <RegisterInfo />,
  'RegisterInfoBallotQuestion': <RegisterInfoBallotQuestion />,
  'RegisterInfoIE': <RegisterInfoIE />,
  'RegisterInfoIEPAC': <RegisterInfoIEPAC />,
  'ReporterInfo': <ReporterInfo />,
  'TreasurerInfo': <TreasurerInfo />,
  'TreasurerTrainingInfo': <TreasurerTrainingInfo />,
  'BankList': <BankList />,
};


//handles adding links into each step
const replaceLinksAndText = (
  text: string,
  currentFilterType: string,
  links?: { placeholder: string; replacements: Record<string, string[]> }[],
) => {
  if (!links) return text;

  let parts: (string | JSX.Element)[] = [];
  let remainingText = text;

  links.forEach(({ placeholder, replacements }, linkIndex) => {
    const placeholderIndex = remainingText.indexOf(placeholder);

    // Ensure the placeholder is found
    if (placeholderIndex !== -1) {
      // Split into text before and after the placeholder
      const beforePlaceholder = remainingText.substring(0, placeholderIndex);
      const afterPlaceholder = remainingText.substring(
        placeholderIndex + placeholder.length
      );

      // Add the text before the link
      parts.push(beforePlaceholder);

      // Select the appropriate URL based on the current filter type
      const replacement = replacements[currentFilterType] || replacements['default'];

      // Add the link itself, stripped of curly braces
      parts.push(
        replacement[0].length > 0 ?
          <a key={`${replacement[1]}-link-${linkIndex}`} href={replacement[0]} target="_blank" rel="noopener noreferrer" >
            {replacement[1] ? replacement[1] : placeholder}
          </a>
          : replacement[1]
      );

      // Update remaining text after the placeholder
      remainingText = afterPlaceholder;
    }
  });

  // Add any remaining text after the last link
  parts.push(remainingText);

  return parts;
};

const GettingStarted = (): JSX.Element => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  const [open, setOpen] = React.useState(false);
  const [openInstruction, setOpenInstruction] = React.useState(false);
  const [info, setInfo] = React.useState<string | undefined>(undefined);


  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);

  };

  const handleDrawerOpenInstruction = (info?: string) => {
    setOpenInstruction(true);
    setInfo(info);

  };

  const handleDrawerCloseInstruction = () => {
    setOpenInstruction(false);
    setInfo(undefined);
  };



  return (
    <Main>
      <Box
        bgcolor={theme.palette.background.default}
        position={'relative'}
        minHeight={'calc(100vh - 247px)'}
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        height={1}
        marginTop={-12}
        paddingTop={12}
      >
        <Container>
          <Box>

            <Typography
              component={'span'}
              variant="h5"
              align={'left'}
              data-aos={'fade-up'}
              gutterBottom
              sx={{
                fontWeight: 700,
              }}
              paddingTop={4}>
              Getting Started: Organize with OCPF
            </Typography>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Typography component={'span'} variant="body1">
                Please choose the candidate and committee type to get step-by-step instructions on how to organize with OCPF.
                There are more than 50 banks participating in Depository Reporting System. Click&nbsp;
                <Link tabIndex={0} sx={{ cursor: 'pointer' }} onClick={() => handleDrawerOpenInstruction('BankList')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleDrawerOpenInstruction('BankList');
                    }
                  }}>here</Link>
                &nbsp;for the list of banks.
              </Typography>

            </Box>
            {
              gettingStartedData.map((filer) => {
                let stepNumber = 0;

                return (
                  <Accordion key={filer.filerId}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel${filer.filerId}-content`}
                      sx={{ ":hover": { backgroundColor: '#f5f5f5' } }}
                    >
                      <Typography component={'span'} variant="h6">
                        {filer.filerType}
                        <Typography component="span" variant="caption" color="text.secondary"> {filer.filerSubtitle}</Typography>
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box component={Paper} sx={{ p: 2, marginBottom: 2 }}>
                        <Typography component={'span'} variant="body1">{filer.filerDescription}</Typography>
                      </Box>

                      {stepsAccordingToFilerType.find(stepSet => stepSet.stepSetId === filerTypeToStep[filer.filerId])?.steps.map((step, index) => {
                        // Check if the step has text (or replacement) for the current filer type; only used if a step is omitted from certain step set for a specific filer type
                        const matchingReplacement = step.linksAndTextReplacements?.find(replacement => {
                          return Object.keys(replacement.replacements).some(key => key === filer.filerType);
                        });

                        if (step.text.length > 1 || (step.linksAndTextReplacements && matchingReplacement)) {
                          stepNumber++;
                        }

                        return (
                          (step.text.length > 1 || (step.linksAndTextReplacements && matchingReplacement)) && (
                            <Typography component={'span'} key={index + '-' + step.infoType} variant="body2">
                              <Paper
                                elevation={1}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  p: 2,
                                  mb: 2,
                                  borderLeft: '5px solid #f5f5f5',
                                }}
                              >
                                <Box
                                  sx={{
                                    bgcolor: stepNumber % 2 === 1 ? '#3f51b5' : '#F87900',
                                    color: 'white',
                                    p: 1,
                                    borderRadius: 1,
                                    mr: 2,
                                    minWidth: '60px',
                                    textAlign: 'center',
                                  }}
                                >
                                  <Typography component={'span'} variant="subtitle1">Step {stepNumber}</Typography>
                                </Box>
                                <Typography component={'span'} variant="body1">
                                  {replaceLinksAndText(step.text, filer.filerType, step.linksAndTextReplacements)}
                                </Typography>
                                <Box sx={{ flexGrow: 1 }} />
                                {step.infoType && (
                                  <IconButton key={step.infoType[filer.filerType] ? step.infoType[filer.filerType] +'-info' : step.infoType['default'] + '-info' + index}
                                    onClick={() => handleDrawerOpenInstruction(
                                      step.infoType?.[filer.filerType]
                                        ? step.infoType?.[filer.filerType]
                                        : step.infoType?.[
                                        'default'
                                        ]
                                    )}
                                    size="small"
                                    sx={{ color: '#2196f3' }}
                                  >
                                    <InfoIcon />
                                  </IconButton>
                                )}
                              </Paper>
                            </Typography>
                          )
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                );
              })
            }



          </Box>

        </Container>
        <InfoDrawer open={openInstruction} handleDrawerClose={handleDrawerCloseInstruction} information={info} componentMap={componentMap} />

      </Box>
    </Main>
  );
};

export default GettingStarted;
